import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteRecord } from "../api/client";
import { invalidateMeasurements } from "../api/invalidation";
import { groupRecordsByLocalDay } from "../lib/recordGroups";
import { formatRecordClock, recordDetail, recordKindLabel, recordValue } from "../lib/records";
import { recordVisuals } from "../lib/recordVisuals";
import type { HealthRecord } from "../types";
import { usePreferences } from "../preferences/usePreferences";
import { EditIcon, TrashIcon } from "./Icons";
import { RecordKindIcon } from "./RecordKindIcon";

interface RecordTimelineProps {
  records: HealthRecord[];
  emptyTitle?: string;
  emptyDescription?: string;
  groupByDay?: boolean;
  onEditRecord?: (record: HealthRecord) => void;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
}

export function RecordTimeline({ records, emptyTitle, emptyDescription, groupByDay = true, onEditRecord, emptyActionLabel, onEmptyAction }: RecordTimelineProps) {
  const { t, i18n } = useTranslation();
  const { weightUnit } = usePreferences();
  const queryClient = useQueryClient();
  const [confirmingId, setConfirmingId] = useState<string>();
  const locale = i18n.resolvedLanguage ?? "en";
  const groups = groupByDay ? groupRecordsByLocalDay(records) : [{ date: "", records }];
  const remove = useMutation({
    mutationFn: deleteRecord,
    onSuccess: async () => {
      await invalidateMeasurements(queryClient);
      setConfirmingId(undefined);
    },
  });

  if (records.length === 0) {
    return (
      <div className="surface-elevated rounded-[1.4rem] border-dashed border-line-strong px-6 py-12 text-center">
        <span className="mx-auto grid size-12 place-items-center rounded-full bg-teal-50 text-xl text-teal-800 ring-1 ring-inset ring-teal-200">↗</span>
        <h3 className="mt-4 text-xl font-semibold">{emptyTitle ?? t("timeline.emptyTitle")}</h3>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-muted">{emptyDescription ?? t("timeline.emptyDescription")}</p>
        {onEmptyAction && emptyActionLabel ? <button type="button" onClick={onEmptyAction} className="pressable mt-5 min-h-11 rounded-xl bg-teal-700 px-5 py-2.5 text-sm font-bold text-canvas hover:bg-teal-800 focus-ring">{emptyActionLabel}</button> : null}
      </div>
    );
  }

  return (
    <div>
      {groups.map((group) => (
        <section key={group.date || "records"} className="timeline-group" aria-label={group.date ? formatGroupDate(group.date, locale) : undefined}>
          {group.date ? (
            <div className="mb-3 flex items-baseline justify-between gap-4 pl-[3rem]">
              <h2 className="text-sm font-bold text-ink">{formatGroupDate(group.date, locale)}</h2>
              <span className="text-xs font-semibold text-muted">{t("calendar.records", { count: group.records.length })}</span>
            </div>
          ) : null}
          <div className="timeline-list space-y-3">
            {group.records.map((record) => {
              const confirming = confirmingId === record.id;
              return (
                <article key={record.id} className="timeline-item relative grid grid-cols-[2.25rem_1fr] gap-3">
                  <div className={`relative z-10 mt-5 grid size-9 place-items-center rounded-full border-4 border-canvas ${recordVisuals[record.kind].iconClass}`}>
                    <RecordKindIcon kind={record.kind} className="size-4" />
                  </div>
                  <div className="group surface-elevated overflow-hidden rounded-[1.2rem] transition-[border-color,box-shadow] duration-200 hover:border-line-strong hover:shadow-[0_1rem_2.5rem_color-mix(in_oklch,var(--color-ink)_7%,transparent)]">
                    <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:p-5">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full px-2.5 py-1 text-[0.65rem] font-black uppercase tracking-[0.12em] ${recordVisuals[record.kind].chipClass}`}>{recordKindLabel(t, record.kind)}</span>
                          <time dateTime={record.recordedAt} className="text-xs font-semibold text-muted">{formatRecordClock(record.recordedAt, locale)}</time>
                        </div>
                        <p className="mt-3 text-2xl font-semibold tracking-[-0.03em] tabular-nums">{recordValue(record, t, locale, weightUnit)}</p>
                        {recordDetail(record, t) ? <p className="mt-1 line-clamp-2 text-sm leading-6 text-muted">{recordDetail(record, t)}</p> : null}
                      </div>
                      <div className="flex shrink-0 self-end sm:self-auto">
                        {onEditRecord ? (
                          <button type="button" onClick={() => onEditRecord(record)} aria-label={t("actions.editRecord", { kind: recordKindLabel(t, record.kind) })} className="pressable grid size-11 place-items-center rounded-xl text-muted opacity-70 hover:bg-teal-50 hover:text-teal-900 hover:opacity-100 focus-ring focus:opacity-100">
                            <EditIcon className="size-4" />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => { remove.reset(); setConfirmingId(record.id); }}
                          disabled={remove.isPending}
                          aria-expanded={confirming}
                          aria-label={t("actions.deleteRecord", { kind: recordKindLabel(t, record.kind) })}
                          className="pressable grid size-11 place-items-center rounded-xl text-muted opacity-70 hover:bg-pulse-soft hover:text-pulse-dark hover:opacity-100 focus-ring focus:opacity-100 disabled:opacity-40"
                        >
                          <TrashIcon className="size-4" />
                        </button>
                      </div>
                    </div>
                    {confirming ? (
                      <div className="flex flex-col gap-3 border-t border-line bg-pulse-soft/55 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5" role="group" aria-label={t("timeline.deleteTitle")}>
                        <p className="text-sm font-semibold text-ink">{t("timeline.deletePrompt")}</p>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setConfirmingId(undefined)} disabled={remove.isPending} className="pressable min-h-9 rounded-lg border border-line-strong bg-surface px-3 text-xs font-bold text-muted hover:text-ink focus-ring">{t("common.cancel")}</button>
                          <button type="button" onClick={() => remove.mutate(record.id)} disabled={remove.isPending} className="pressable min-h-9 rounded-lg bg-vitals px-3 text-xs font-bold text-white hover:brightness-110 focus-ring disabled:cursor-wait disabled:opacity-60">{remove.isPending ? t("timeline.deleting") : t("timeline.confirmDelete")}</button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}
      {remove.isError ? <p role="alert" className="mt-4 rounded-xl bg-pulse-soft px-4 py-3 text-sm font-semibold text-pulse-dark">{t("timeline.deleteFailed")}</p> : null}
    </div>
  );
}

function formatGroupDate(date: string, locale: string): string {
  const label = new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(new Date(`${date}T12:00:00`));
  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
}
